import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { PaginationParams } from '~/types/pagination'
import type { AuthArgs } from '~/api/models/index'

interface GetAllActionsArgs extends PaginationParams {
  categories: string[]
}
interface GetAssetsByActionIdArgs extends PaginationParams {
  id: number
}

const queries = {
  getAll: {
    queryFn: (args: Ref<GetAllActionsArgs>) =>
      useApi().actions.getAllActions(
        {
          per_page: args.value.per_page,
          current_page: args.value.current_page
        },
        args.value.categories
      ),
    queryKey: (args: Ref<GetAllActionsArgs>) => [
      'allActions',
      {
        current_page: args.value.current_page,
        per_page: args.value.per_page,
        categories: args.value.categories
      }
    ]
  },
  getById: {
    queryFn: (args: { id: number }) => useApi().actions.getActionById(args.id),
    queryKey: (args: { id: number }) => ['actionById', String(args.id)]
  },
  getAssetsByActionId: {
    queryFn: (args: Ref<GetAssetsByActionIdArgs>) =>
      useApi().actions.getActionAssetsById(args.value.id, {
        per_page: args.value.per_page,
        current_page: args.value.current_page
      }),
    queryKey: (args: Ref<GetAssetsByActionIdArgs>) => [
      'assetsByActionId',
      {
        id: args.value.id,
        current_page: args.value.current_page,
        per_page: args.value.per_page
      }
    ]
  }
} as const
const mutations = {
  cancel: {
    request: (args: { id: number }) => useApi().actions.cancelAction(args.id),
    cacheKey: (args: { id: number }) => [
      ['allActions'],
      ['actionById', String(args.id)],
      ['assetsByActionId', String(args.id)]
    ]
  },
  retry: {
    request: (args: { id: number }) => useApi().actions.retryAction(args.id),
    cacheKey: (args: { id: number }) => [
      ['allActions'],
      ['actionById', String(args.id)],
      ['assetsByActionId', String(args.id)]
    ]
  },
  cancelAsset: {
    request: (args: { actionId: number; assetId: number }) =>
      useApi().actions.cancelActionAsset(args.actionId, args.assetId),
    cacheKey: (args: { actionId: number; assetId: number }) => [
      ['allActions'],
      ['actionById', String(args.actionId)],
      ['assetsByActionId', String(args.actionId)]
    ]
  },
  retryAsset: {
    request: (args: { actionId: number; assetId: number }) =>
      useApi().actions.retryActionAsset(args.actionId, args.assetId),
    cacheKey: (args: { actionId: number; assetId: number }) => [
      ['allActions'],
      ['actionById', String(args.actionId)],
      ['assetsByActionId', String(args.actionId)]
    ]
  }
} as const

export type ActionService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): ActionService =>
  defineService(authArgs, {
    queries,
    mutations
  })
